import { useState, useMemo, useCallback } from 'react'
import { Yakureki } from '@/shared/types/yakurekiCore'
import { getDateRangeFromYakurekiList } from '@/shared/helpers'
import { localStorageWrapper } from '@/shared/helpers/localStorage'
import { usePharmacistsQuery } from '@/features/auth'
import { PharmacistListResponse } from '@/features/auth/types/pharmacy'

const PHARMACIST_FILTER_ALL = '__all__'

// --- フィルタチェーン ---
// yakurekiList (raw)
//   → dateFilteredItems     (日付)
//   → pharmacistFiltered    (薬剤師)
//   → dismissedFiltered     (完了非表示)  ← 最終表示リスト

interface UseDrawerFiltersReturn {
  // 最終表示リスト
  filteredList: Yakureki[]
  // 完了（dismissed）の件数
  dismissedCount: number

  // 日付フィルタ
  date: Date
  minDate: Date | null
  isBackDisabled: boolean
  isForwardDisabled: boolean
  isToday: boolean
  handleDateBack: () => void
  handleDateForward: () => void
  handleJumpToToday: () => void

  // 完了表示フィルタ（デフォルトOFF = dismissed非表示）
  showDismissed: boolean
  handleShowDismissedToggle: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void

  // 薬剤師フィルタ
  pharmacistData: PharmacistListResponse | null
  filterPharmacistId: string
  handlePharmacistFilterChange: (pharmacistId: string) => void
}

export const PHARMACIST_FILTER_ALL_VALUE = PHARMACIST_FILTER_ALL

export const useDrawerFilters = (
  yakurekiList: Yakureki[]
): UseDrawerFiltersReturn => {
  // ─── 日付フィルタ ───
  const [date, setDate] = useState(new Date())
  const { minDate } = useMemo(
    () => getDateRangeFromYakurekiList(yakurekiList),
    [yakurekiList]
  )

  const handleDateBack = useCallback(() => {
    setDate((prev) => {
      const d = new Date(prev)
      d.setDate(d.getDate() - 1)
      return d
    })
  }, [])

  const handleDateForward = useCallback(() => {
    setDate((prev) => {
      const d = new Date(prev)
      d.setDate(d.getDate() + 1)
      return d
    })
  }, [])

  const handleJumpToToday = useCallback(() => {
    setDate(new Date())
  }, [])

  const isDateInRange = useCallback(
    (targetDate: Date) => {
      const today = new Date()
      const todayDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      )
      const target = new Date(
        targetDate.getFullYear(),
        targetDate.getMonth(),
        targetDate.getDate()
      )
      if (target > todayDate) return false
      if (!minDate) return false
      const min = new Date(
        minDate.getFullYear(),
        minDate.getMonth(),
        minDate.getDate()
      )
      return target >= min && target <= todayDate
    },
    [minDate]
  )

  const previousDate = useMemo(() => {
    const prev = new Date(date)
    prev.setDate(prev.getDate() - 1)
    return prev
  }, [date])

  const nextDate = useMemo(() => {
    const next = new Date(date)
    next.setDate(next.getDate() + 1)
    return next
  }, [date])

  const isBackDisabled = !isDateInRange(previousDate)
  const isForwardDisabled = !isDateInRange(nextDate)

  const isToday = useMemo(() => {
    const today = new Date()
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    )
  }, [date])

  // ─── 完了表示フィルタ（デフォルトOFF = dismissed非表示） ───
  const [showDismissed, setShowDismissed] = useState<boolean>(false)

  const handleShowDismissedToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setShowDismissed(event.target.checked)
    },
    []
  )

  // ─── 薬剤師フィルタ ───
  const { pharmacists: pharmacistData } = usePharmacistsQuery()
  const [filterPharmacistId, setFilterPharmacistId] = useState<string>(
    localStorageWrapper.getItem('filterPharmacistId') ?? PHARMACIST_FILTER_ALL
  )

  const handlePharmacistFilterChange = useCallback((pharmacistId: string) => {
    setFilterPharmacistId(pharmacistId)
    localStorageWrapper.setItem('filterPharmacistId', pharmacistId)
  }, [])

  // ─── フィルタチェーン適用 ───
  const dateFilteredItems = useMemo(() => {
    return yakurekiList.filter((item) => {
      const itemDate = new Date(item.created_at)
      return (
        itemDate.getFullYear() === date.getFullYear() &&
        itemDate.getMonth() === date.getMonth() &&
        itemDate.getDate() === date.getDate()
      )
    })
  }, [date, yakurekiList])

  const pharmacistFiltered = useMemo(() => {
    if (filterPharmacistId === PHARMACIST_FILTER_ALL) return dateFilteredItems
    return dateFilteredItems.filter(
      (item) => !item.pharmacist_id || item.pharmacist_id === filterPharmacistId
    )
  }, [dateFilteredItems, filterPharmacistId])

  const filteredList = useMemo(() => {
    if (showDismissed) return pharmacistFiltered
    return pharmacistFiltered.filter((item) => item.status !== 'dismissed')
  }, [pharmacistFiltered, showDismissed])

  const dismissedCount = useMemo(() => {
    return pharmacistFiltered.filter((item) => item.status === 'dismissed')
      .length
  }, [pharmacistFiltered])

  return {
    filteredList,
    dismissedCount,
    date,
    minDate,
    isBackDisabled,
    isForwardDisabled,
    isToday,
    handleDateBack,
    handleDateForward,
    handleJumpToToday,
    showDismissed,
    handleShowDismissedToggle,
    pharmacistData,
    filterPharmacistId,
    handlePharmacistFilterChange,
  }
}
